/**
 * The browser-side reader for the mobile enrollment envelope (#1244 §4.1).
 *
 * This is the **second** implementation of that parser; the first is KMP's
 * `WorkshopJoinCode.parseAt`, which is the one that actually guards a
 * credential. The two exist for genuinely different jobs and the difference is
 * worth stating, because "there are two parsers" is otherwise a smell:
 *
 * - KMP's is a **security boundary**. It validates `api=` against the compiled-in
 *   host allowlist before any request carrying the invitation token leaves the
 *   device, and nothing downstream of it can be reached without passing it.
 * - this one is **presentation**. It exists so the landing page can say "this
 *   link is for Demo Autohuolto on api.demo.oceanspace.fi" and hand the payload
 *   on unchanged. It deliberately does **not** re-implement the host allowlist:
 *   duplicating that list in the portal would create a second copy to keep in
 *   step with the app, and it would buy nothing — the app re-parses and
 *   re-validates the payload it is handed, and its confirm screen names the host
 *   again before the first call. A link this page is happy to display can still
 *   be refused by the app, which is the correct order of authority.
 *
 * What it must match exactly is the **envelope**: which key means what, that the
 * first duplicate of a key wins, and that a malformed percent escape degrades to
 * the raw text rather than throwing. Anywhere the two disagree, a link works on
 * one side and not the other — so the shared cases are pinned in tests on both.
 *
 * Nothing here touches the network. The payload carries a one-time invitation
 * token (or a 90-second login code) in the URL fragment specifically so it stays
 * in the browser; sending it anywhere, including to our own API, would defeat
 * the reason it is in the fragment at all.
 */

/** Which secret the envelope carries, and therefore what the app will do with it. */
export type MobileJoinKind = 'invite' | 'login' | 'pointer';

export interface MobileJoinPayload {
  kind: MobileJoinKind;
  /** Validated only as "present and parseable as a URL"; see the note above. */
  apiBaseUrl: string;
  /** Host alone, for display — what a human is asked to recognise. */
  apiHost: string;
  /** The payload's `name=`. Attacker-supplied; a label, never authority. */
  workshopName: string | null;
  /** The invited address, when the minter included it. */
  email: string | null;
  expiresAt: Date | null;
  /**
   * The invitation token, and **only** when `kind === 'invite'`.
   *
   * Present because the browser fallback (`/accept-invitation?token=…`) needs
   * it. A login code is deliberately *not* exposed: there is no browser flow
   * that can redeem one, so surfacing it would put a live session credential
   * into a link for no reason.
   */
  inviteToken: string | null;
  /**
   * The fragment exactly as it arrived, for the app handoff.
   *
   * Passed through verbatim rather than re-serialized from the parsed fields: a
   * round trip through this parser would silently drop any key it does not know
   * about, so a link minted by a newer staff-api would reach the app with fields
   * missing — and the app, not this page, is what decides which fields matter.
   */
  fragment: string;
}

export type MobileJoinParse =
  | { status: 'valid'; payload: MobileJoinPayload }
  /** Not one of our links: no fragment, no `api=`, unreadable, or two secrets. */
  | { status: 'invalid' }
  | { status: 'unsupportedVersion'; version: string }
  | { status: 'expired'; expiredAt: Date };

/** The envelope version this build reads. Must track `WorkshopJoinCode.SUPPORTED_VERSION`. */
export const SUPPORTED_VERSION = 1;

function hexDigit(char: string): number | null {
  if (char >= '0' && char <= '9') return char.charCodeAt(0) - 48;
  if (char >= 'a' && char <= 'f') return char.charCodeAt(0) - 87;
  if (char >= 'A' && char <= 'F') return char.charCodeAt(0) - 55;
  return null;
}

/**
 * Percent-decode over UTF-8 bytes, **mirroring KMP's `percentDecode` exactly**.
 *
 * Not `decodeURIComponent`, which is all-or-nothing: it throws on the first bad
 * escape, so `Auto%20%ZZ` came back as the raw `Auto%20%ZZ` in the browser while
 * the app read `Auto %ZZ`. Same link, two labels. This walks run-by-run like
 * Kotlin does — valid escapes around a malformed one still decode — and treats
 * only two hex digits as an escape, so `%-1` and `%+1` stay literal on both sides.
 *
 * The shared cases are pinned in `mobileJoinPayload.test.ts` and in
 * `WorkshopJoinCodeTest.kt`, each naming the other.
 */
function percentDecode(value: string): string {
  if (!value.includes('%')) return value;

  const bytes: number[] = [];
  const utf8 = new TextEncoder();
  let i = 0;
  while (i < value.length) {
    let escape: number | null = null;
    if (value[i] === '%' && i + 2 < value.length) {
      const high = hexDigit(value[i + 1] as string);
      const low = hexDigit(value[i + 2] as string);
      escape = high === null || low === null ? null : high * 16 + low;
    }
    if (escape !== null) {
      bytes.push(escape);
      i += 3;
    } else {
      // A stray '%' (or a bad escape) is kept verbatim rather than failing the
      // whole parse; run-at-a-time so surrogate pairs survive.
      const found = value.indexOf('%', i + 1);
      const next = found < 0 ? value.length : found;
      for (const byte of utf8.encode(value.slice(i, next))) bytes.push(byte);
      i = next;
    }
  }
  return new TextDecoder().decode(new Uint8Array(bytes));
}

/**
 * `&`-separated `key=value` pairs, percent-decoded.
 *
 * Two behaviours are copied from the KMP parser rather than chosen here:
 *
 * - **the first occurrence of a key wins**, so a crafted
 *   `…&api=<good>&api=<evil>` cannot override the value the user just read;
 * - **a malformed escape is kept verbatim** while valid escapes around it still
 *   decode — see `percentDecode`, which mirrors Kotlin's walk rather than using
 *   `decodeURIComponent`.
 */
function parseFragment(fragment: string): Map<string, string> {
  const result = new Map<string, string>();
  for (const pair of fragment.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const key = eq < 0 ? pair : pair.slice(0, eq);
    const rawValue = eq < 0 ? '' : pair.slice(eq + 1);
    if (!key || result.has(key)) continue;
    result.set(key, percentDecode(rawValue));
  }
  return result;
}

/**
 * The version as Kotlin's `String.toIntOrNull()` would read it, or null.
 *
 * That accepts an optional sign and nothing else — no whitespace, no decimal
 * point, no hex. `Number()` would accept `" 1 "`, `1.0` and `0x1`, which would
 * make this *looser* than the app instead of merely different.
 */
function parseVersion(raw: string): number | null {
  if (!/^[+-]?\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function nonBlank(value: string | undefined): string | null {
  if (value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : value;
}

/**
 * Split an enrollment URL into the part before the fragment and the fragment.
 *
 * Accepts both shapes the link can arrive in: the canonical
 * `https://<portal>/m/join#…` a mail client or QR carries, and the
 * `os-buddy://join#…` form the *app* accepts when a code is pasted into it.
 *
 * Reading the second shape here does **not** mean this page ever writes one — it
 * does not, deliberately (see `APP_URL_SCHEME` below). It is accepted so that a
 * link pasted back into a browser reads the same as it would in the app, rather
 * than looking broken.
 */
function fragmentOf(raw: string): string | null {
  const trimmed = raw.trim();
  const hash = trimmed.indexOf('#');
  if (hash >= 0) {
    const fragment = trimmed.slice(hash + 1);
    return fragment === '' ? null : fragment;
  }
  // A URL with no fragment carries no payload — **even if its query string looks
  // like one**. Falling through to "treat the whole string as a fragment" would
  // accept `…/m/join?a=b&v=1&api=…&invite=…`, quietly reinstating the query-string
  // delivery the envelope exists to forbid. Only a string that is not a URL at
  // all is read as a bare fragment, which is what `location.hash` gives us.
  if (trimmed.includes('://')) return null;
  return trimmed === '' ? null : trimmed;
}

/**
 * Read a raw enrollment URL — or a bare fragment, which is what
 * `location.hash` gives us minus its leading `#`.
 *
 * @param nowMs Injected so the expiry branch is testable without faking a clock.
 */
export function parseMobileJoinPayload(raw: string, nowMs: number = Date.now()): MobileJoinParse {
  const fragment = fragmentOf(raw);
  if (fragment === null) return { status: 'invalid' };

  const params = parseFragment(fragment);

  const version = nonBlank(params.get('v'));
  if (version === null) return { status: 'invalid' };
  // Compared as an **integer**, matching `WorkshopJoinCode.parseAt`'s
  // `toIntOrNull()`. An exact string compare made `v=01` "update required" here
  // while the app accepted the same link happily — the divergence is only visible
  // when Universal Links are active, which is the worst way to find it.
  if (parseVersion(version) !== SUPPORTED_VERSION) {
    return { status: 'unsupportedVersion', version };
  }

  const apiRaw = nonBlank(params.get('api'));
  if (apiRaw === null) return { status: 'invalid' };

  let apiHost: string;
  let apiBaseUrl: string;
  try {
    const url = new URL(apiRaw.includes('://') ? apiRaw : `https://${apiRaw}`);
    // Userinfo is how a URL is made to *read* as one host and *resolve* to
    // another, and this page's whole contribution is letting a human read the
    // host. KMP rejects it outright; so does this.
    if (url.protocol !== 'https:' || url.username || url.password) return { status: 'invalid' };
    apiHost = url.host;
    apiBaseUrl = url.origin;
  } catch {
    return { status: 'invalid' };
  }

  const invite = nonBlank(params.get('invite'));
  const loginCode = nonBlank(params.get('code'));
  // Two secrets in one envelope is malformed. Guessing which one wins is how a
  // crafted link aims the app at whichever path it prefers.
  if (invite !== null && loginCode !== null) return { status: 'invalid' };

  const kind: MobileJoinKind = invite !== null ? 'invite' : loginCode !== null ? 'login' : 'pointer';

  // An unparseable `exp` degrades to "no local expiry" rather than rejecting a
  // link the server would still honour — the server stays authoritative either
  // way. A parseable one that has passed is reported, because telling someone
  // their link is expired beats sending them into the app to find out.
  const expRaw = nonBlank(params.get('exp'));
  const expMs = expRaw === null ? NaN : Date.parse(expRaw);
  const expiresAt = Number.isNaN(expMs) ? null : new Date(expMs);
  if (expiresAt !== null && expMs <= nowMs) {
    return { status: 'expired', expiredAt: expiresAt };
  }

  return {
    status: 'valid',
    payload: {
      kind,
      apiBaseUrl,
      apiHost,
      workshopName: nonBlank(params.get('name')),
      email: nonBlank(params.get('email')),
      expiresAt,
      inviteToken: invite,
      fragment,
    },
  };
}

/**
 * The URL scheme the app registers. Kebab-case per `docs/NAMING.md`.
 *
 * Exported for the parser's benefit only — `fragmentOf` accepts a pasted
 * `os-buddy://join#…` link. **Nothing in the portal builds one**, and there is no
 * `appHandoffUrl` helper here any more: a custom scheme has no owner, so emitting
 * a payload into one hands it to whichever app claimed the scheme. See the long
 * comment in `MobileJoinPage.tsx` and design §5.5. The app reaches this page's
 * payload by Universal Link, or the user finishes in the browser.
 */
export const APP_URL_SCHEME = 'os-buddy';
